<template>
  <div class="light:bg-gray-100 h-full p-2 flex items-top justify-top">
    <UCard class="m-4 w-md">
      <template #header>
        <h2>Instructions</h2>
      </template>

      <div v-if="user && user.user_metadata">
        <p class="text-center p-5">
          Welcome back, {{ user.user_metadata.name }}
        </p>
      </div>

      <UAlert
        v-show="!user"
        class="mb-4"
        variant="subtle"
        color="warning"
        title="You are not currently logged in"
      >
        <template #description>
          You can <NuxtLink to="/login" class="underline">log in</NuxtLink> or
          <NuxtLink to="/register" class="underline">register</NuxtLink> to
          store your game results!.
        </template>
      </UAlert>

      <GameHelp />
    </UCard>

    <UCard v-show="newGame == true" class="m-4 w-lg dark:bg-gray-800" > 
      <template #header>
        <h2>Players</h2>
      </template>

      <div >
        <h4 class="text-center mb-3">Players</h4>

        <UButtonGroup class="w-full">
          <UInput
            id="new-player"
            v-model="newPlayer"
            color="neutral"
            variant="outline"
            placeholder="catanmaster982"
            class="grow"
            @keydown.enter="AddPlayer"
          />
          <UButton color="neutral" label="Add Player" @click="AddPlayer" />
        </UButtonGroup>

        <div class="players pt-2 mb-6">
          <div
            v-if="game.players.length == 0"
            class="p-5 rounded-sm text-center mt-3"
          >
            <span class="text-gray-400 text-sm">no players yet...</span>
          </div>

          <div v-else>
            <div
              v-for="player in game.players"
              :key="player.id"
              class="player m-1 p-1 flex"
            >
              <span class="grow">{{ player.name }}</span>
              <UButton
                size="sm"
                icon="i-lucide-trash"
                variant="ghost"
                color="primary"
                class="custom-class"
                @click="RemovePlayer(player.name)"
              />
            </div>
          </div>
        </div>
      </div>
    </UCard>
    <UCard class="m-4">
      <template #header>
        <h2>Game Options</h2>
      </template>
      <!-- <h4 class="text-center mb-3">Game Options</h4> -->

      <UFormField
        label="Exchange rate mode"
        description="Choose when you want exchange rates to update."
      >
        <URadioGroup
          v-model="game.settings.exchangeRateMode"
          class="mt-3"
          value-key="id"
          :items="exchangeRateModeOptions"
        />
      </UFormField>

      <UFormField
        label="Enable Events"
        description="Oregon trail style random events that throw a kink in your game"
      >
        <USwitch
          v-model="game.settings.eventsEnabled"
          :label="game.settings.eventsEnabled ? 'ON' : 'OFF'"
          class="pt-2"
        />
      </UFormField>

      <UFormField
        v-show="game.settings.eventsEnabled"
        :label="`Event intensity - ${game.settings.eventsIntensity}%`"
        description="How often should events happen? ie about 10% of rolls will have an event if set to 10%."
      >
        <USlider
          v-model="game.settings.eventsIntensity"
          :min="0"
          :max="100"
          :default-value="10"
          class="pt-2"
          tooltip
          :step="5"
        />
      </UFormField>

      <UButton
        class="w-full justify-center mt-5"
        trailing-icon="i-lucide-arrow-right"
        size="xl"
        @click="SaveGame"
      >
        {{ newGame ? "Begin Game" : "Return to game" }}</UButton
      >
    </UCard>
  </div>
</template>

<script lang="ts" setup>
import { UFormField, UInput, UButton } from "#components";
import { v4 as uuidv4 } from "uuid";
// import type { Game } from '../models/Game'
// import type { Player } from '../models/Player'
import type * as Models from "~~/models";
import type { RadioGroupItem } from "@nuxt/ui";
import { useGameStore } from "~/stores/gameStore";

const user = useSupabaseUser();
const toast = useToast();
const gameStore = useGameStore();
const newPlayer = ref("");
//const colorMode = useColorMode();
let newGame = false;

//alt.. init with store
const route = useRoute();
const gameID = route.query.gameid as string | undefined; // may be undefined

if (gameID == undefined) {
  gameStore.newGame();
  newGame = true;
} else {
  gameStore.getGame(gameID);
}

const game: Models.Game = reactive(gameStore.game);

const exchangeRateModeOptions = ref<RadioGroupItem[]>([
  {
    label: "Random",
    description:
      "Exchange rates will all independently change at random intervals",
    id: "random",
  },
  {
    label: "On player turn",
    description: "Exchange rates will change with every turn",
    id: "player_turn",
  },
]);
// const value = ref<RadioGroupValue>('light');

function AddPlayer() {
  if (newPlayer.value.length === 0) {
    toast.add({
      title: "Please enter a name",
      description: "You have a name don't you?",
      icon: "i-lucide-triangle-alert",
      color: "error",
    });
    return;
  } else if (game.players.some((p) => p["name"] === newPlayer.value)) {
    toast.add({
      title: "This name already exists",
      description: "You are going to have to come up with something different",
      icon: "i-lucide-triangle-alert",
      color: "error",
    });
    return;
  }

  //add player
  game.players.push({ id: uuidv4(), name: newPlayer.value });
  newPlayer.value = "";
  const element = document.querySelector("#new-player") as HTMLInputElement;
  element.focus();
}

function RemovePlayer(player: string) {
  game.players = game.players.filter((p) => p.name !== player);
}

async function SaveGame() {
  gameStore.game = game;

  //validate players
  if(game.players.length == 0){
    toast.add({
      title: "No players set",
      description: "You need to add players in order to play a game",
      icon: "i-lucide-triangle-alert",
      color: "error",
    });
    return;
  }

  //save
  if (newGame == true) {
    const saveResult = await gameStore.saveGame();
    console.log("Game saved, ready to begin", saveResult);
    if (saveResult.success == false) {
      toast.add({
        title: "Unable to start game",
        description:
          "Something has gone terribly wrong and the game cannot be started: " +
          saveResult.error,
        icon: "i-lucide-triangle-alert",
        color: "error",
      });
    } else {
      navigateTo("/game/" + game.id);
    }
  } else {
    const saveResult = await gameStore.saveSettings();
    console.log("Settings saved", saveResult);
    if (saveResult.success == false) {
      toast.add({
        title: "Unable to save settings",
        description:
          "Something has gone terribly wrong and the settings cannot be saved: " +
          saveResult.error,
        icon: "i-lucide-triangle-alert",
        color: "error",
      });
    } else {
      navigateTo("/game/" + game.id);
    }
  }
}
</script>

<style></style>
